import { Location, LocationStrategy } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { BarcodeFormat } from '@zxing/library';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { GooglePlaceDirective } from 'ngx-google-places-autocomplete';
import { ToastrService } from 'ngx-toastr';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { VinInfo } from 'src/app/helpers';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LocaleService } from 'src/app/services/locale/locale.service';
import { LocationService } from 'src/app/services/location/location.service';
import { SharedService } from 'src/app/services/shared/shared.service';
import { UserService } from 'src/app/services/user-service/user.service';

import * as moment from 'moment';
import { IsDeviceOnlineService } from 'src/app/services/is-online/is-online.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-vin-information',
  templateUrl: './vin-information.component.html',
  styleUrls: ['./vin-information.component.scss']
})
export class VinInformationComponent implements OnInit {
  public submitted: boolean = false;
  public submitLoad: boolean = false;
  public searchSuccess: boolean = false;
  public lang: string = null;

  isDeviceOnline = false;

  public vin: UntypedFormGroup = null;
  @ViewChild('vinNum', { static: false }) vinNum: ElementRef;

  @ViewChild('featureNotAvailableModalTemplate', { static: true }) featureNotAvailableModal:
    | TemplateRef<void>
    | undefined;

  public vinSearchResults: object[] = [];
  public vinInfo: VinInfo = new VinInfo();
  public subscriptions: Subscription[] = [];
  public storedDatabaseVersion = 0.0;

  // Scanner
  @ViewChild('scanner', { static: false }) scanner: ZXingScannerComponent;
  public scannerOn: boolean = false;
  public hasPermission: boolean;
  public hasDevices: boolean;
  public availableDevices: MediaDeviceInfo[] = [];
  public currentDevice: MediaDeviceInfo = null;
  public formats: BarcodeFormat[] = [
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.RSS_14,
    BarcodeFormat.ITF,
    BarcodeFormat.CODABAR,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.QR_CODE
  ];
  private code: string = null;
  // END

  modalRef?: BsModalRef | null;

  // Address
  @ViewChild('placesRef') placesRef: GooglePlaceDirective;
  public formattedAddress: string = null;
  public addressProps: Places = {
    street_number: null,
    route: null,
    locality: null,
    sublocality_level_1: null,
    sublocality_level_2: null,
    country: null
  };
  public cities: object[] = [];
  public areas: object[] = [];
  // END

  constructor(
    public formBuilder: UntypedFormBuilder,
    public router: Router,
    public locationStrategy: LocationStrategy,
    public authService: AuthService,
    public userService: UserService,
    public sharedService: SharedService,
    public locationService: LocationService,
    public localeService: LocaleService,
    public toastr: ToastrService,
    private readonly _isDeviceOnlineService: IsDeviceOnlineService,
    private readonly _location: Location,
    private readonly _modalService: BsModalService
  ) {
    this.vin = this.formBuilder.group({
      vin_number: [
        '',
        [
          Validators.required,
          Validators.maxLength(21),
          Validators.pattern(/^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$/) //Alphanumeric RegExp
        ]
      ],
      plate_number: [
        '',
        [Validators.required, Validators.pattern(/^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9 ]+)$/)]
      ],
      location: [''],
      city: ['', [Validators.required]],
      area: ['', [Validators.required]],
      vehicle_type: [null]
    });

    this.AuthUserData();
  }

  ngOnInit() {
    this.localeService.localeSubject.subscribe((res) => {
      if (res) {
        this.lang = res;
      }
    });

    this._isDeviceOnlineService.networkStatus$.subscribe({
      next: (value) => {
        switch (value) {
          case 'online': {
            this.isDeviceOnline = true;
            break;
          }
          case 'offline': {
            this.isDeviceOnline = false;
            // this.refreshPage();
            this.getCities();
            break;
          }
        }
      }
    });

    this.getCities();

    this.vin.get('location').valueChanges.subscribe({
      next: (valueChanges) => {
        if (this._isDeviceOnlineService.currentNetwork === 'offline') {
          this.formattedAddress = valueChanges;
        }
      }
    });
    // this.getVinDetails();

    this.storedDatabaseVersion = Number(localStorage.getItem('pwa-local-database-version'));
  }

  refreshPage() {
    // window.location.reload();
    this._location.replaceState(this._location.path());
  }

  ngAfterViewInit() {
    const vinDebounce = (ev: string) => {
      const vinSearchDebounce = fromEvent(this.vinNum.nativeElement, ev)
        .pipe(debounceTime(300))
        .subscribe((res: Event) => {
          const query: string = res.target['value'];
          const regexBlankSpace: RegExp = new RegExp('^ ');

          if (!query || regexBlankSpace.test(query)) {
            this.searchSuccess = false;
            return false;
          }

          if (this._isDeviceOnlineService.currentNetwork === 'online') {
            this.searchVIN(query);
          }
        });
      this.subscriptions.push(vinSearchDebounce);
    };
    vinDebounce('input');
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  AuthUserData() {
    const authSub = this.authService.user.subscribe((res) => {
      if (res) {
        let userDetails = res['user']['user_data'];
      }
    });

    this.subscriptions.push(authSub);
  }

  get formData() {
    return this.vin.controls;
  }

  openModal(template: TemplateRef<void>) {
    this.modalRef = this._modalService.show(template, { id: 1 });
  }

  closeModal(modalId?: number) {
    this._modalService.hide(modalId);
  }

  // Scanner
  isCamera(devices: MediaDeviceInfo[]) {
    this.availableDevices = devices;
    this.hasDevices = Boolean(devices && devices.length);
  }

  permission(permission: boolean) {
    this.hasPermission = permission;
  }

  scanSuccess(result: string) {
    if (result) {
      this.code = result;
      this.vin.get('vin_number').setValue(this.code);
      this.searchVIN(result);
      this.toastr.success('VIN received successfully.', 'Success');
    }
  }

  scanStart() {
    this.scannerOn = !this.scannerOn;

    if (this.availableDevices.length) {
      if (this.scannerOn) this.currentDevice = this.availableDevices[1];
      else this.currentDevice = null;
    }
  }
  // END

  // Location
  placesOption: any = {
    componentRestrictions: { country: ['SA', 'AE'] },
    fields: ['address_components', 'geometry', 'icon', 'name'],
    types: []
  };

  getPosition() {
    if (this._isDeviceOnlineService.currentNetwork === 'offline') {
      this.openModal(this.featureNotAvailableModal);
    } else {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.getLocation(position);
        });
      }
    }
  }

  getLocation(position: any) {
    const lat: number = position.coords.latitude;
    const lng: number = position.coords.longitude;

    this.locationService.geocodeLocation(lat, lng).subscribe(
      (res) => {
        let status: string = res.status;
        let addresses: any[] = res.results;

        if (status === 'OK') {
          addresses.forEach((address: any, index: number) => {
            if (address.geometry.location_type === 'GEOMETRIC_CENTER') {
              document.querySelector('[name="location"]')['value'] = address.formatted_address;
              this.getPlaces(address);
            }
          });
        }
      },
      (error) => {}
    );
  }

  getPlaces(locObj: any) {
    console.log('inside getPlaces');
    console.log(locObj);

    const addresses: any[] = locObj.address_components;
    let addressParts: string[] = [];

    addresses.forEach((address: any, index: number) => {
      addressParts.push(address.long_name);
      this.formattedAddress = addressParts.join(', ');

      if (address.types && address.type?.length) {
        for (let [key, value] of address.types.entries()) {
          switch (address.types[key]) {
            case 'street_number':
              this.addressProps.street_number = address.long_name;
              break;

            case 'route':
              this.addressProps.route = address.long_name;
              break;

            case 'locality':
              this.addressProps.locality = address.long_name;
              break;

            case 'sublocality_level_1':
              this.addressProps.sublocality_level_1 = address.long_name;
              break;

            case 'sublocality_level_2':
              this.addressProps.sublocality_level_2 = address.long_name;
              break;

            case 'country':
              this.addressProps.country = address.long_name;
              break;
          }
        }
      }
    });
  }

  getCities() {
    this.locationService.getCities().subscribe(
      (res) => {
        if (res) {
          let success: boolean = res['success'];
          let data: object[] = res['data'];

          if (success && Object.keys(data).length) {
            this.cities = data;
          }
        }
      },
      (error: HttpErrorResponse) => {}
    );
  }

  getAreas(ev: any): Promise<object[]> {
    let value: string = ev && ev.target ? ev.target.value : ev;
    let data: object = {
      city_id: value
    };

    return new Promise<object[]>((resolve, reject) => {
      this.locationService.getAreas(data).subscribe(
        (res) => {
          if (res) {
            let success: boolean = res['success'];
            let data: object[] = res['data'];

            if (success) {
              this.areas = data;
              resolve(this.areas);
            } else {
              reject('No Data');
            }
          }
        },
        (error: HttpErrorResponse) => {
          reject(error);
        }
      );
    });
  }
  // END

  searchVIN(vin: string) {
    const data: object = {
      vin_number: vin
    };

    this.userService.vinSearch(data).subscribe(
      (res) => {
        if (res) {
          let success: boolean = res['success'];
          let data: object = res['data'];

          if (success && Object.keys(data).length) {
            if (data['vehicle'].length && data['vehicle_count']) {
              this.searchSuccess = true;
              this.vinSearchResults = data['vehicle'].map((item: object) => ({
                ...item,
                created_at: moment(item['created_at']).format('DD/MM/YYYY h:m:s a'),
                updated_at: moment(item['updated_at']).format('DD/MM/YYYY h:m:s a')
              }));
            } else {
              this.searchSuccess = false;
            }
          }
        }
      },
      (error: HttpErrorResponse) => {
        this.toastr.error('Something went wrong', 'Failed');
      }
    );
  }

  selectedVinObj: object = {};
  vinDataSelector(vinObj: object) {
    this.searchSuccess = false;
    this.formattedAddress = vinObj['location'];
    this.vin.get('vin_number').setValue(vinObj['vin_number'] || '');
    this.vin.get('plate_number').setValue(vinObj['plate_number'] || '');
    this.vin.get('location').setValue(vinObj['location'] || '');
    this.vin.get('city').setValue(vinObj['city'] || '');

    this.getAreas(vinObj['city'])
      .then((res) => {
        this.selectedVinObj = vinObj;

        setTimeout(() => {
          this.vin.get('area').setValue(vinObj['area']);
        }, 0);
      })
      .catch((res) => {
        this.selectedVinObj = vinObj;
      });
  }

  vinSubmit() {
    this.submitted = true;

    if (this.vin.invalid) {
      return false;
    } else {
      const vinData = {
        vehicle_id: Object.keys(this.selectedVinObj).length
          ? this.selectedVinObj['vehicle_id']
          : '',
        vin_number: this.vin.get('vin_number').value,
        plate_number: this.vin.get('plate_number').value,
        location: this.formattedAddress,
        city: this.vin.get('city').value,
        area: this.vin.get('area').value,
        is_new: this.vin.get('vehicle_type').value === 'new',
        is_salvage: this.vin.get('vehicle_type').value === 'salvage'
      };

      this.submitLoad = true;
      this.userService.PostVinInfo(vinData).subscribe(
        (res) => {
          let success = res['success'];
          let data = res['data'];

          /*------------ Debasish Modified Code--------------*/

          let vinvehicleId = res['vehicle_id'];

          /*------------ Debasish Modified Code--------------*/

          if (success) {
            this.submitLoad = false;
            this.toastr.success('Car details updated succssfully!', 'Success');

            this.sharedService.setVehicleId = res['vehicle_id'];

            if (vinData.is_new) this.router.navigate(['/validation']);
            else if (
              !vinData.is_new &&
              this.selectedVinObj['estimara_expiry_date'] &&
              this.selectedVinObj['estimara_image'] &&
              this.selectedVinObj['mvpi_expiry_date'] &&
              this.selectedVinObj['mvpi_image'] &&
              this.selectedVinObj['odo_value'] &&
              this.selectedVinObj['odo_image'] &&
              this.selectedVinObj['vin_image'] &&
              this.selectedVinObj['plate_image']
            )
              this.router.navigate(['/vam/information']);
            else this.router.navigate(['/validation']);
          } else {
            this.submitLoad = false;
          }
        },
        (error) => {
          this.submitLoad = false;
        }
      );
    }
  }

  goToConnectivityTestRoute() {
    this.router.navigate(['/connectivity-test']);
  }

  goToOfflineDataPushRoute() {
    this.router.navigate(['/vam/offline-data']);
  }

  logout() {
    this.authService.setStatus({ status: true }).subscribe();
    this.authService.Logout();
    this.router.navigate(['/']);
  }
}

interface Places {
  street_number: number;
  route: string;
  locality: string;
  sublocality_level_1: string;
  sublocality_level_2: string;
  country: string;
}
